import { useRef } from "react";
import { Palette, Upload, X } from "lucide-react";
import { cn } from "@/lib/utils";

import type { LeadFormData } from "../types";

interface Props {
  data: LeadFormData;
  onChange: (patch: Partial<LeadFormData>) => void;
}

const MAX_BYTES = 10 * 1024 * 1024; // 10MB
const ACCEPT = "image/png,image/jpeg,image/jpg,image/svg+xml";

export function StepBranding({ data, onChange }: Props) {
  const inputRef = useRef<HTMLInputElement>(null);

  const handleFile = (file: File | undefined) => {
    if (!file) return;
    if (file.size > MAX_BYTES) return;
    onChange({ logoFile: file });
  };

  return (
    <div className="space-y-6">
      <div className="space-y-3">
        <div className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-brand-blue">
          <Palette className="h-4 w-4" />
          Branding
        </div>
        <h2 className="text-balance text-3xl font-bold tracking-tighter text-foreground sm:text-4xl">
          Got any branding assets?
        </h2>
        <p className="text-base text-muted-foreground">
          Totally optional - if you don't have these, we'll work with you to create something amazing.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-6 md:grid-cols-[30%_1fr]">
        <div className="space-y-2">
          <label className="text-sm font-semibold text-foreground">Upload your logo</label>

          <input
            ref={inputRef}
            type="file"
            accept={ACCEPT}
            className="hidden"
            onChange={(e) => handleFile(e.target.files?.[0])}
          />

          {data.logoFile ? (
            <div className="flex items-center justify-between gap-3 rounded-2xl border border-brand-blue/40 bg-brand-blue/5 px-4 py-3">
              <div className="flex min-w-0 items-center gap-3">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-brand-blue/10 text-brand-blue">
                  <Upload className="h-5 w-5" />
                </div>
                <div className="min-w-0">
                  <div className="truncate text-sm font-medium text-gray-950">
                    {data.logoFile.name}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {(data.logoFile.size / 1024).toFixed(0)} KB
                  </div>
                </div>
              </div>
              <button
                type="button"
                onClick={() => {
                  onChange({ logoFile: undefined });
                  if (inputRef.current) inputRef.current.value = "";
                }}
                className="rounded-full p-2 text-muted-foreground transition-colors hover:bg-background hover:text-foreground"
                aria-label="Remove logo"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              className={cn(
                "flex h-[180px] w-full flex-col items-center justify-center gap-2 rounded-2xl border-2 border-dashed border-border bg-muted/30 px-6 text-center transition-all",
                "hover:border-brand-blue hover:bg-brand-blue/5",
              )}
            >
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-background text-muted-foreground">
                <Upload className="h-5 w-5" />
              </div>
              <div className="text-sm font-medium text-gray-950">Click to upload logo</div>
              <div className="text-xs text-muted-foreground">PNG, JPG, SVG - max 10MB</div>
            </button>
          )}
        </div>

        <div className="space-y-2">
          <label className="text-sm font-semibold text-foreground">
            Brand colors or style preferences
          </label>
          <textarea
            value={data.brandStyle ?? ""}
            onChange={(e) => onChange({ brandStyle: e.target.value })}
            placeholder="e.g. Navy blue & gold, modern & clean, dark & moody..."
            maxLength={500}
            className="h-[180px] w-full resize-none rounded-2xl border border-input bg-background px-4 py-3 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          />
        </div>
      </div>
    </div>
  );
}
